import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { createAdapter } from '@/adapters';
import { applyImport } from '@/adapters/csvImport';
import { DEFAULT_RATES, withRates } from '@/fixtures/generator';
import { KEYS, readJson, writeJson } from './storage';

const DataContext = createContext(null);

/**
 * Holder valgt adapter, grunndatasett per scenario, importoverlegg (økt) og demosatser.
 * Grunndata muteres aldri; import og satser legges på som nye kopier.
 */
export function DataProvider({ scenarioId, children }) {
  const [adapterKind, setAdapterKindState] = useState(() => readJson(window.localStorage, KEYS.adapter, 'mock'));
  const [failMode, setFailMode] = useState(false);
  const [status, setStatus] = useState('loading');
  const [error, setError] = useState(null);
  const [baseDataset, setBaseDataset] = useState(null);
  const [sources, setSources] = useState([]);
  const [importRows, setImportRowsState] = useState(() => readJson(window.localStorage, KEYS.importRows(scenarioId), null));
  const [ratesOverride, setRatesOverrideState] = useState(() => readJson(window.localStorage, KEYS.rates, null));
  const [syncState, setSyncState] = useState(() => readJson(window.localStorage, KEYS.syncState, {}));
  const [reloadToken, setReloadToken] = useState(0);
  const adapterRef = useRef(null);

  useEffect(() => {
    setImportRowsState(readJson(window.localStorage, KEYS.importRows(scenarioId), null));
  }, [scenarioId]);

  useEffect(() => {
    let cancelled = false;
    const adapter = createAdapter(adapterKind, { failMode });
    adapterRef.current = adapter;
    setStatus('loading');
    setError(null);
    const started = performance.now();
    Promise.all([adapter.getDataset(scenarioId), adapter.getSourceStatus(scenarioId)])
      .then(([ds, src]) => {
        if (cancelled) return;
        const elapsed = performance.now() - started;
        const finish = () => {
          if (cancelled) return;
          setBaseDataset(ds);
          setSources(src);
          setStatus('ready');
        };
        // rolig skjelett i minst 120 ms for å unngå blink
        if (elapsed < 120) setTimeout(finish, 120 - elapsed);
        else finish();
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e?.message ?? 'Ukjent feil ved henting av data');
        setStatus('error');
      });
    return () => {
      cancelled = true;
    };
  }, [adapterKind, failMode, scenarioId, reloadToken]);

  const retry = useCallback(() => setReloadToken((t) => t + 1), []);

  const setAdapterKind = useCallback((kind) => {
    writeJson(window.localStorage, KEYS.adapter, kind);
    setAdapterKindState(kind);
  }, []);

  const setImportRows = useCallback(
    (rows) => {
      if (rows && rows.length) writeJson(window.localStorage, KEYS.importRows(scenarioId), rows);
      else window.localStorage.removeItem(KEYS.importRows(scenarioId));
      setImportRowsState(rows && rows.length ? rows : null);
    },
    [scenarioId],
  );

  const setRates = useCallback((rates) => {
    if (rates) writeJson(window.localStorage, KEYS.rates, rates);
    else window.localStorage.removeItem(KEYS.rates);
    setRatesOverrideState(rates);
  }, []);

  const recordSync = useCallback((sourceId, entry) => {
    setSyncState((prev) => {
      const next = { ...prev, [sourceId]: entry };
      writeJson(window.localStorage, KEYS.syncState, next);
      return next;
    });
  }, []);

  const importResult = useMemo(() => {
    if (!baseDataset) return null;
    if (!importRows || !importRows.length) return { dataset: baseDataset, summary: null, history: [] };
    return applyImport(baseDataset, importRows, baseDataset.frozenNow);
  }, [baseDataset, importRows]);

  const rates = useMemo(() => ({ ...DEFAULT_RATES, ...(ratesOverride ?? {}), isIllustrative: true, provenance: ratesOverride ? 'Antatt sats (endret av dataadministrator), ikke kundens sats' : DEFAULT_RATES.provenance }), [ratesOverride]);

  const dataset = useMemo(() => {
    if (!importResult) return null;
    const ds = importResult.dataset;
    const merged = { ...withRates(ds, rates), sources: sources.length ? sources : ds.sources };
    return merged;
  }, [importResult, rates, sources]);

  const effectiveSources = useMemo(() => {
    const base = sources.length ? sources : baseDataset?.sources ?? [];
    return base.map((s) => (syncState[s.sourceId] ? { ...s, ...syncState[s.sourceId] } : s));
  }, [sources, baseDataset, syncState]);

  const resetLocalData = useCallback(() => {
    setImportRowsState(null);
    setRatesOverrideState(null);
    setSyncState({});
    setAdapterKindState('mock');
    setFailMode(false);
  }, []);

  const value = useMemo(
    () => ({
      adapterKind,
      adapterLabel: adapterRef.current?.label ?? '',
      setAdapterKind,
      failMode,
      setFailMode,
      status,
      error,
      retry,
      dataset,
      baseDataset,
      sources: effectiveSources,
      importRows,
      setImportRows,
      importSummary: importResult?.summary ?? null,
      importHistory: importResult?.history ?? [],
      rates,
      ratesOverridden: !!ratesOverride,
      setRates,
      recordSync,
      resetLocalData,
    }),
    [adapterKind, setAdapterKind, failMode, status, error, retry, dataset, baseDataset, effectiveSources, importRows, setImportRows, importResult, rates, ratesOverride, setRates, recordSync, resetLocalData],
  );
  return <DataContext.Provider value={value}>{children}</DataContext.Provider>;
}

export function useData() {
  const ctx = useContext(DataContext);
  if (!ctx) throw new Error('useData utenfor DataProvider');
  return ctx;
}
